import React from 'react';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
} from '@material-ui/icons';
import styles from './VideoPanel.module.css';

const VideoPanel = ({
  formatFileSize,
  selectedVideos = [],
  onClearSelection,
  onPlayVideo,
  onEditVideo,
  onDeleteVideo,
  onDownloadVideo,
  onBulkDownload,
  onDownloadAlbums,
  onDownloadPhotos,
  selectedItems,
  setOpenEditInBulk,
  tabTitle,
  isDownloadDisabled = false,
  isCheckingDownload = false,
  downloadingFiles = [],
  itemType = 'video',
}) => {
  const videoCount = selectedItems.length;
  const firstVideo = selectedItems[0];
  const isPhoto = itemType === 'photo';

  // Albums: check if the selected albums are already downloading
  const getAlbumId = (a) => a?.content_id;
  const isAlbumDownloading = (aid) => downloadingFiles.some(
    (f) => f.albumId === aid && (f.status === 'downloading' || f.status === 'pending')
  );
  const areAllAlbumsDownloading = !isPhoto && videoCount > 0
    && selectedItems.every((a) => isAlbumDownloading(getAlbumId(a)));

  const photoTitle = (p) => p?.title || p?.image?.split('/').pop() || 'Photo';
  const photoThumb = (p) => p?.imagekit_url || p?.image || '/Assets/no-image-available.png';

  // Check if specific selected videos are already downloading
  const getVideoId = (v) => v?.content_id || v?.video_id || v?.draft_id;
  const isVideoDownloading = (vid) => downloadingFiles.some(
    (f) => (f.videoId || f.id) === vid && (f.status === 'downloading' || f.status === 'pending')
  );

  const singleVideoId = videoCount === 1 ? getVideoId(firstVideo) : null;
  const isSingleVideoDownloading = singleVideoId && isVideoDownloading(singleVideoId);

  // For bulk: check if ALL selected videos are already downloading
  const areAllSelectedDownloading = videoCount > 1
    && selectedItems.every((v) => isVideoDownloading(getVideoId(v)));
  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>
            {isPhoto
              ? (videoCount === 1 ? 'Photo Selected' : `${videoCount} Photos Selected`)
              : tabTitle === 0
                ? (videoCount === 1 ? 'Video Selected' : `${videoCount} Videos Selected`)
                : (videoCount === 1 ? 'Album Selected' : `${videoCount} Albums Selected`)}
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClearSelection}
            title="Clear selection"
          >
            <CloseIcon />
          </button>
        </div>
      </div>
      <div className={styles.content}>
        {videoCount === 1 && firstVideo ? (
          // Single item preview
          <div className={styles.videoPreview}>
            <div className={styles.thumbnailContainer}>
              <img
                src={isPhoto
                  ? photoThumb(firstVideo)
                  : (firstVideo.thumbnail_url || firstVideo.presigned_url || firstVideo.thumbnail || firstVideo.thumbnails)}
                alt={isPhoto ? photoTitle(firstVideo) : (firstVideo.title || 'Video thumbnail')}
                className={styles.thumbnail}
                onError={(e) => {
                  e.target.src = '/Assets/no-image-available.png';
                }}
              />
              {/* <button
                className={styles.playButton}
                onClick={() => onPlayVideo(firstVideo)}
                title="Play video"
              >
                <PlayIcon />
              </button> */}
            </div>
            {isPhoto && (
              <div className={styles.videoInfo}>
                <h3 className={styles.videoTitle}>{photoTitle(firstVideo)}</h3>
              </div>
            )}
            {tabTitle === 0 && !isPhoto && (
            <div className={styles.videoInfo}>
              <h3 className={styles.videoTitle}>
                {firstVideo.title || 'Untitled Video'}
              </h3>

                <div className={styles.videoDetails}>
                  {firstVideo.video_duration !== null && (
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Duration:</span>
                    <span className={styles.detailValue}>
                    {firstVideo.video_duration || 'Unknown'}
                    </span>
                </div>
                  )}
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Size:</span>
                    <span className={styles.detailValue}>
                      {formatFileSize(firstVideo.file_size || firstVideo.video_size)}
                    </span>
                </div>

                {/* <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Status:</span>
                  <span className={`${styles.detailValue} ${styles.statusBadge} ${firstVideo.status === 'processed' ? styles.processed : styles.processing}`}>
                    {firstVideo.status || 'Unknown'}
                  </span>
                </div> */}

{(firstVideo?.created_at || firstVideo?.created) && (
  <div className={styles.detailItem}>
    <span className={styles.detailLabel}>Published:</span>
    <span className={styles.detailValue}>
      {(() => {
        const date = new Date(firstVideo.created_at || firstVideo.created);
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      })()}
    </span>
  </div>
)}
                </div>
            </div>
              )}
          </div>
        ) : videoCount > 1 ? (
          // Multiple items with stacked thumbnails
          <div className={styles.multipleVideos}>
            <div className={styles.stackedThumbnails}>
              {selectedItems.slice(0, 3).map((video, index) => (
                <div
                  key={isPhoto
                    ? (video.id || index)
                    : (video.content_id || video.draft_id || video.video_id || index)}
                  className={styles.stackedThumbnail}
                  style={{ zIndex: 3 - index, transform: `translateX(${index * 8}px) translateY(${index * 8}px)` }}
                >
                  <img
                    src={isPhoto
                      ? photoThumb(video)
                      : (video.thumbnail_url || video.presigned_url || video.thumbnail || video.thumbnails)}
                    alt={isPhoto ? photoTitle(video) : (video.title || 'Video thumbnail')}
                    className={styles.stackedImage}
                    onError={(e) => {
                      e.target.src = '/Assets/no-image-available.png';
                    }}
                  />
                </div>
              ))}
              {videoCount > 3 && (
                <div className={styles.moreIndicator}>
                  +
                  {videoCount - 3}
                </div>
              )}
            </div>
            <div className={styles.summaryText}>
              <p className={styles.videoCount}>
                {videoCount}
                {' '}
                {isPhoto ? 'photos' : (tabTitle === 0 ? 'videos' : 'albums')}
                {' '}
                selected
              </p>
                {tabTitle === 0 && !isPhoto && (

                  <p className={styles.summarySubtext}>
                Use the action buttons below to perform bulk operations
                  </p>
              )}
            </div>
          </div>
        ) : null}

        {/* Action Buttons */}
        <div className={styles.actions}>
          {/* Single video: Edit button */}
          {videoCount === 1 && !isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.editButton}`}
              onClick={() => { onEditVideo(firstVideo); onClearSelection(); }}
              title="Edit video"
            >
              <EditIcon />
              <span>Edit</span>
            </button>
          )}

          {/* Multiple videos: Edit button */}
          {selectedItems?.length > 1 && tabTitle === 0 && !isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.EditInBulkButton}`}
              onClick={() => { setOpenEditInBulk(true); }}
              title={`Edit ${videoCount} Videos`}
            >
              <EditIcon />
              <span>
                Edit
                {' '}
                {videoCount}
                {' '}
                Videos
              </span>
            </button>
          )}

          {/* Single video: Download button */}
          {videoCount === 1 && tabTitle === 0 && !isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.downloadButton} ${isDownloadDisabled || isCheckingDownload || isSingleVideoDownloading ? styles.disabled : ''}`}
              onClick={() => onDownloadVideo(selectedItems)}
              title={isSingleVideoDownloading ? 'This video is downloading...' : isCheckingDownload ? 'Checking availability...' : isDownloadDisabled ? 'Download not available' : 'Download video'}
              disabled={isDownloadDisabled || isCheckingDownload || isSingleVideoDownloading}
            >
              <DownloadIcon />
              <span>{isSingleVideoDownloading ? 'Downloading...' : isCheckingDownload ? 'Checking...' : 'Download'}</span>
            </button>
          )}

          {/* Multiple videos: Download button */}
          {selectedItems?.length > 1 && tabTitle === 0 && !isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.downloadButton} ${areAllSelectedDownloading ? styles.disabled : ''}`}
              onClick={() => onBulkDownload(selectedItems)}
              title={areAllSelectedDownloading ? 'These videos are downloading...' : `Download ${videoCount} Videos`}
              disabled={areAllSelectedDownloading}
            >
              <DownloadIcon />
              <span>
                {areAllSelectedDownloading ? 'Downloading...' : `Download ${videoCount} Videos`}
              </span>
            </button>
          )}

          {/* Single album: Download button */}
          {videoCount === 1 && tabTitle === 1 && !isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.downloadButton} ${areAllAlbumsDownloading ? styles.disabled : ''}`}
              onClick={() => onDownloadAlbums(selectedItems)}
              title={areAllAlbumsDownloading ? 'This album is downloading...' : 'Download album'}
              disabled={areAllAlbumsDownloading}
            >
              <DownloadIcon />
              <span>{areAllAlbumsDownloading ? 'Downloading...' : 'Download'}</span>
            </button>
          )}

          {/* Multiple albums: Download button */}
          {selectedItems?.length > 1 && tabTitle === 1 && !isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.downloadButton} ${areAllAlbumsDownloading ? styles.disabled : ''}`}
              onClick={() => onDownloadAlbums(selectedItems)}
              title={areAllAlbumsDownloading ? 'These albums are downloading...' : `Download ${videoCount} Albums`}
              disabled={areAllAlbumsDownloading}
            >
              <DownloadIcon />
              <span>
                {areAllAlbumsDownloading ? 'Downloading...' : `Download ${videoCount} Albums`}
              </span>
            </button>
          )}

          {/* Photos: Download button (single and multiple) */}
          {isPhoto && (
            <button
              className={`${styles.actionButton} ${styles.downloadButton}`}
              onClick={() => onDownloadPhotos && onDownloadPhotos(selectedItems)}
              title={videoCount === 1 ? 'Download photo' : `Download ${videoCount} Photos`}
            >
              <DownloadIcon />
              <span>{videoCount === 1 ? 'Download' : `Download ${videoCount} Photos`}</span>
            </button>
          )}

          {/* Delete button (both single and multiple) */}
          <button
            className={`${styles.actionButton} ${styles.deleteButton}`}
            onClick={() => onDeleteVideo(selectedItems)}
            title={videoCount === 1 ? 'Delete video' : 'Delete videos'}
          >
            <DeleteIcon />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPanel;
